import React, { useEffect, useState } from 'react';
import { X, AlertCircle, ShieldCheck, CalendarClock, ClipboardEdit, CalendarPlus, ToggleLeft, Eye } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import DateInput from '../ui/DateInput';
import MediaEventDetail from '../media/MediaCalendar/MediaEventDetail';
import {
  MEDIA_EVENT_CATEGORIES, MEDIA_EVENT_TONES, MEDIA_DURATION_OPTIONS, addHoursToLocal, localToIso, isoToLocal,
} from '../../lib/media';

// §10 admin overrides (F7). Compact action bars + modals for the media admin
// overrides, surfaced on the per-account Media tab. Each modal posts to the
// matching /api/admin/media/* endpoint and calls onChanged() to refresh.

function Modal({ title, icon: Icon, onClose, children, wide = false }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
      <div className={`w-full ${wide ? 'max-w-2xl' : 'max-w-md'} bg-[#0a0a0a] border border-white/[0.08] rounded-2xl shadow-2xl`}>
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
          <h2 className="text-base font-semibold text-white tracking-tight inline-flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-[#B0FC38]" />} {title}
          </h2>
          <button type="button" onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] text-white text-[13px] focus:outline-none focus:border-[#B0FC38]/40';
const labelCls = 'block text-[11px] uppercase tracking-wider text-gray-500 mb-1.5';
const chipBtn = 'inline-flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] border border-white/[0.08] text-[10px] text-gray-300 hover:text-white hover:border-[#B0FC38]/30';

function Footer({ onClose, submitting, submitLabel, danger }) {
  return (
    <div className="flex items-center justify-end gap-2 pt-3 mt-1 border-t border-white/[0.05]">
      <button type="button" onClick={onClose} className="px-4 py-2 text-[12px] text-gray-400 hover:text-white">Cancel</button>
      <button type="submit" disabled={submitting}
        className={`px-4 py-2 rounded-full text-[13px] font-medium disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-500 text-white' : 'bg-[#B0FC38] hover:bg-[#9BE22E] text-gray-900'}`}>
        {submitting ? 'Saving…' : submitLabel}
      </button>
    </div>
  );
}

function ErrBanner({ text }) {
  if (!text) return null;
  return (
    <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-[12px] text-red-300 flex items-start gap-2 mb-3">
      <AlertCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {text}
    </div>
  );
}

// Generic submit wrapper: runs fn, surfaces errors, closes + refreshes on success.
function useSubmit(onChanged, onClose) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const run = async (fn) => {
    if (submitting) return;
    setSubmitting(true); setError('');
    try {
      await fn();
      onChanged?.();
      onClose?.();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : (err?.message || 'Failed'));
      setSubmitting(false);
    }
  };
  return { submitting, error, run };
}

// ---------------- Event detail hydration ----------------

// Admin list endpoints ship a lightweight summary without the questionnaire
// (contacts, venue, parking, description, shots, equipment). Anything that
// READS or EDITS the request must hydrate from the detail endpoint first —
// editing from the summary would overwrite the client's answers with blanks.
export function useAdminEventDetail(eventId) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(!!eventId);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!eventId) return undefined;
    let alive = true;
    setLoading(true); setError('');
    api.get(`/api/admin/media/events/${eventId}`)
      .then((d) => { if (alive) setDetail(d?.event || null); })
      .catch((err) => { if (alive) setError(err?.message || 'Failed to load request'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eventId]);
  return { detail, loading, error };
}

// Read-only "what did the school actually ask for" view. Merges the summary
// row (so the modal paints instantly) with the hydrated detail.
export function EventRequestModal({ event, onClose, actions = [] }) {
  const { detail, loading, error } = useAdminEventDetail(event?.id);
  const merged = { ...(event || {}), ...(detail || {}) };
  const submitted = detail?.createdByName || detail?.createdByEmail
    ? `${detail.createdByName || detail.createdByEmail}${detail.createdAt ? ` · ${String(detail.createdAt).slice(0, 10)}` : ''}`
    : null;
  return (
    <MediaEventDetail
      event={merged}
      onClose={onClose}
      actions={actions}
      banner={error ? { tone: 'error', text: error } : loading ? { tone: 'muted', text: 'Loading the full request…' } : null}
      submittedBy={submitted}
    />
  );
}

// ---------------- Event overrides ----------------

export function EventOverrides({ event, onChanged }) {
  const [open, setOpen] = useState(null); // 'view' | 'approve' | 'reschedule' | 'questionnaire'
  const close = () => setOpen(null);
  const canApprove = event.approvalState === 'pending';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <button type="button" className={chipBtn} onClick={() => setOpen('view')} data-testid={`event-view-request-${event.id}`}>
        <Eye className="w-3 h-3" /> View request
      </button>
      {canApprove && (
        <button type="button" className={`${chipBtn} border-amber-400/30 text-amber-300`} onClick={() => setOpen('approve')}>
          <ShieldCheck className="w-3 h-3" /> Approve booking
        </button>
      )}
      <button type="button" className={chipBtn} onClick={() => setOpen('reschedule')}><CalendarClock className="w-3 h-3" /> Force reschedule</button>
      <button type="button" className={chipBtn} onClick={() => setOpen('questionnaire')}><ClipboardEdit className="w-3 h-3" /> Edit questionnaire</button>

      {open === 'view' && (
        <EventRequestModal
          event={event}
          onClose={close}
          actions={[
            ...(canApprove ? [{ label: 'Approve / Reject', variant: 'primary', onClick: () => setOpen('approve') }] : []),
            { label: 'Edit questionnaire', onClick: () => setOpen('questionnaire') },
          ]}
        />
      )}
      {open === 'approve' && <ApproveBookingModal event={event} onChanged={onChanged} onClose={close} />}
      {open === 'reschedule' && <ForceRescheduleModal event={event} onChanged={onChanged} onClose={close} />}
      {open === 'questionnaire' && <QuestionnaireModal event={event} onChanged={onChanged} onClose={close} />}
    </div>
  );
}

// DOE rework — every booking awaits approval. Approving requires choosing who
// shoots it: an internal media user (conflict-checked server-side — a 409 with
// the clash details lands in the error banner) or an external fallback.
export function ApproveBookingModal({ event, onChanged, onClose }) {
  const [shooters, setShooters] = useState([]);
  const [mode, setMode] = useState('internal'); // 'internal' | 'external'
  const [assignedUserId, setAssignedUserId] = useState('');
  const [extName, setExtName] = useState('');
  const [extPhone, setExtPhone] = useState('');
  const [extEmail, setExtEmail] = useState('');
  const [reason, setReason] = useState('');
  const { submitting, error, run } = useSubmit(onChanged, onClose);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/api/admin/users?role=media&include_admins=1');
        if (cancelled) return;
        const list = (res.users || []).filter((u) => u.isActive !== false);
        setShooters(list);
        if (list.length === 1) setAssignedUserId(list[0].id);
      } catch { /* picker degrades to external-only */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const approve = () => run(() => api.post(`/api/admin/media/events/${event.id}/approve`, {
    decision: 'approve',
    ...(mode === 'internal'
      ? { assignedUserId }
      : { externalFallbackName: extName.trim(), externalFallbackPhone: extPhone.trim(), externalFallbackEmail: extEmail.trim() || undefined }),
  }));
  const reject = () => run(() => api.post(`/api/admin/media/events/${event.id}/approve`, {
    decision: 'reject', reason: reason.trim() || undefined,
  }));

  const approveDisabled = submitting || (mode === 'internal' ? !assignedUserId : !extName.trim() || !extPhone.trim());

  return (
    <Modal title="Approve booking" icon={ShieldCheck} onClose={onClose}>
      <div className="px-5 py-4">
        <ErrBanner text={error} />
        <p className="text-[12px] text-gray-400 mb-3">
          {event.eventTitle}. Approving books it immediately for the shooter you pick (their calendar is conflict-checked). Rejecting cancels it and releases the school's credit.
        </p>

        <div className="flex items-center gap-1 mb-3">
          <button type="button" onClick={() => setMode('internal')}
            className={`px-2.5 py-1 rounded-md text-[11px] border ${mode === 'internal' ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-white' : 'bg-white/[0.02] border-white/[0.08] text-gray-400'}`}>
            Media team
          </button>
          <button type="button" onClick={() => setMode('external')}
            className={`px-2.5 py-1 rounded-md text-[11px] border ${mode === 'external' ? 'bg-[#B0FC38]/15 border-[#B0FC38]/30 text-white' : 'bg-white/[0.02] border-white/[0.08] text-gray-400'}`}>
            External shooter
          </button>
        </div>

        {mode === 'internal' ? (
          <div className="mb-3">
            <label className={labelCls}>Assign to</label>
            {shooters.length === 0 ? (
              <p className="text-[12px] text-gray-500">No active media users. Use the External shooter tab.</p>
            ) : (
              <div className="space-y-1.5" data-testid="approve-shooter-picker">
                {shooters.map((u) => (
                  <label key={u.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-[13px] ${assignedUserId === u.id ? 'bg-[#B0FC38]/10 border-[#B0FC38]/30 text-white' : 'bg-white/[0.02] border-white/[0.06] text-gray-300'}`}>
                    <input type="radio" name="approve-shooter" className="accent-[#B0FC38]" checked={assignedUserId === u.id} onChange={() => setAssignedUserId(u.id)} />
                    {u.name} <span className="text-[11px] text-gray-500">{u.email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2 mb-3">
            <div><label className={labelCls}>Name</label><input value={extName} onChange={(e) => setExtName(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Phone</label><input value={extPhone} onChange={(e) => setExtPhone(e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>Email (optional)</label><input value={extEmail} onChange={(e) => setExtEmail(e.target.value)} className={inputCls} /></div>
          </div>
        )}

        <label className={labelCls}>Reject reason (only used if rejecting)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} placeholder="Shared with the school on rejection" />

        <div className="flex items-center justify-end gap-2 pt-3 mt-1 border-t border-white/[0.05]">
          <button type="button" onClick={reject} disabled={submitting} className="px-4 py-2 rounded-full text-[13px] text-red-300 border border-red-500/30 hover:bg-red-500/10 disabled:opacity-50">Reject booking</button>
          <button type="button" onClick={approve} disabled={approveDisabled} className="px-4 py-2 rounded-full bg-[#B0FC38] text-black text-[13px] font-medium hover:bg-[#9fe632] disabled:opacity-50">{submitting ? 'Saving…' : 'Approve & book'}</button>
        </div>
      </div>
    </Modal>
  );
}

function ForceRescheduleModal({ event, onChanged, onClose }) {
  const [start, setStart] = useState(toLocal(event.scheduledStartAt));
  const [end, setEnd] = useState(toLocal(event.scheduledEndAt));
  const [reason, setReason] = useState('');
  const { submitting, error, run } = useSubmit(onChanged, onClose);
  const onSubmit = (e) => {
    e.preventDefault();
    run(() => api.post(`/api/admin/media/events/${event.id}/force-reschedule`, {
      newScheduledStartAt: fromLocal(start), newScheduledEndAt: fromLocal(end), reason: reason.trim() || undefined,
    }));
  };
  return (
    <Modal title="Force reschedule" icon={CalendarClock} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-5 py-4">
        <ErrBanner text={error} />
        <p className="text-[12px] text-gray-400 mb-3">Moves {event.eventTitle} without school approval. The notice tier is recomputed from the new date.</p>
        <label className={labelCls}>New start</label>
        <DateInput withTime required value={start} onChange={setStart} className={`${inputCls} mb-3`} />
        <label className={labelCls}>New end</label>
        <DateInput withTime required value={end} onChange={setEnd} className={`${inputCls} mb-3`} />
        <label className={labelCls}>Reason (optional)</label>
        <input value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls} />
        <Footer onClose={onClose} submitting={submitting} submitLabel="Reschedule" />
      </form>
    </Modal>
  );
}

// Mirrors the portal booking form (MediaBookingForm) field-for-field — same
// order, labels and option lists — so staff correct exactly what the school
// filled in. Saving is a content edit only: the PATCH never changes status or
// approval, so a pending request stays pending until someone approves it.
const Q_TEXT_KEYS = [
  'eventTitle', 'locationAddress', 'locationVenueName',
  'primaryContactName', 'primaryContactPhone', 'primaryContactRole',
  'backupContactName', 'backupContactPhone',
  'parkingInstructions', 'eventDescription', 'eventCategory', 'tone',
  'priorityShots', 'equipmentNotes',
];

function hoursBetween(startIso, endIso) {
  const a = new Date(startIso); const b = new Date(endIso);
  if (Number.isNaN(a.valueOf()) || Number.isNaN(b.valueOf())) return 2;
  return Math.round(((b - a) / 3600_000) * 2) / 2 || 2;
}

function QField({ label, hint, children }) {
  return (
    <div>
      <label className={labelCls}>{label}</label>
      {children}
      {hint && <span className="block text-[11px] text-gray-500 mt-1">{hint}</span>}
    </div>
  );
}

function QuestionnaireModal({ event, onChanged, onClose }) {
  // Hydrate from the detail endpoint — the list row carries no questionnaire
  // fields, and saving a form seeded from it would blank the client's answers.
  const { detail, loading, error: loadError } = useAdminEventDetail(event.id);
  const [form, setForm] = useState(null);
  useEffect(() => {
    if (!detail) return;
    const start = isoToLocal(detail.scheduledStartAt);
    const durationHours = hoursBetween(detail.scheduledStartAt, detail.scheduledEndAt);
    setForm({
      ...Object.fromEntries(Q_TEXT_KEYS.map((key) => [key, detail[key] ?? ''])),
      locationOutsideNyc: !!detail.locationOutsideNyc,
      scheduledStartAt: start,
      durationHours,
      scheduledEndAt: addHoursToLocal(start, durationHours),
    });
  }, [detail]);
  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setStart = (v) => setForm((f) => ({ ...f, scheduledStartAt: v, scheduledEndAt: addHoursToLocal(v, f.durationHours) }));
  const setDuration = (h) => setForm((f) => ({ ...f, durationHours: h, scheduledEndAt: addHoursToLocal(f.scheduledStartAt, h) }));

  const { submitting, error, run } = useSubmit(onChanged, onClose);
  const onSubmit = (e) => {
    e.preventDefault();
    if (!form || !detail) return;
    // Send only what actually changed so untouched answers are never rewritten.
    const patch = {};
    for (const key of Q_TEXT_KEYS) {
      const next = String(form[key] ?? '').trim();
      const prev = String(detail[key] ?? '').trim();
      if (next !== prev) patch[key] = next === '' ? null : next;
    }
    if (form.locationOutsideNyc !== !!detail.locationOutsideNyc) patch.locationOutsideNyc = form.locationOutsideNyc;
    const nextStart = localToIso(form.scheduledStartAt);
    const nextEnd = localToIso(form.scheduledEndAt);
    const prevStart = localToIso(isoToLocal(detail.scheduledStartAt));
    const prevEnd = localToIso(isoToLocal(detail.scheduledEndAt));
    if (nextStart && nextEnd && (nextStart !== prevStart || nextEnd !== prevEnd)) {
      patch.scheduledStartAt = nextStart;
      patch.scheduledEndAt = nextEnd;
    }
    if (Object.keys(patch).length === 0) { onClose?.(); return; }
    run(() => api.patch(`/api/admin/media/events/${event.id}/questionnaire`, patch));
  };

  const durationOptions = form && !MEDIA_DURATION_OPTIONS.some((o) => o.value === form.durationHours)
    ? [...MEDIA_DURATION_OPTIONS, { value: form.durationHours, label: `${form.durationHours} hours` }]
    : MEDIA_DURATION_OPTIONS;

  return (
    <Modal title="Edit questionnaire" icon={ClipboardEdit} onClose={onClose} wide>
      <form onSubmit={onSubmit} className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto" data-testid="questionnaire-form">
        <ErrBanner text={error || loadError} />
        {!form && !loadError && <div className="text-[12px] text-gray-500">Loading the school's answers…</div>}
        {form && (
          <>
            <QField label="Event title">
              <input value={form.eventTitle} data-testid="q-eventTitle" onChange={(e) => set('eventTitle', e.target.value)} className={inputCls} />
            </QField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QField label="Start date & time">
                <div data-testid="q-scheduledStartAt">
                  <DateInput withTime value={form.scheduledStartAt} onChange={setStart} className={inputCls} />
                </div>
              </QField>
              <QField label="Duration" hint={form.scheduledEndAt ? `Ends ${new Date(form.scheduledEndAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })} · coverage capped at 4 hours.` : null}>
                <select value={form.durationHours} data-testid="q-durationHours" onChange={(e) => setDuration(Number(e.target.value))} className={inputCls}>
                  {durationOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </QField>
            </div>

            <QField label="Location address">
              <input value={form.locationAddress} data-testid="q-locationAddress" onChange={(e) => set('locationAddress', e.target.value)} className={inputCls} />
            </QField>
            <QField label="Venue name (optional)">
              <input value={form.locationVenueName} data-testid="q-locationVenueName" onChange={(e) => set('locationVenueName', e.target.value)} className={inputCls} />
            </QField>
            <label className="inline-flex items-start gap-2 text-[12px] text-gray-400 cursor-pointer">
              <input type="checkbox" checked={form.locationOutsideNyc} data-testid="q-locationOutsideNyc"
                onChange={(e) => set('locationOutsideNyc', e.target.checked)} className="mt-0.5 w-3.5 h-3.5 rounded accent-[#B0FC38]" />
              <span>Event is <strong className="text-gray-300">outside the five boroughs of NYC</strong>.</span>
            </label>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QField label="Primary contact name">
                <input value={form.primaryContactName} data-testid="q-primaryContactName" onChange={(e) => set('primaryContactName', e.target.value)} className={inputCls} />
              </QField>
              <QField label="Primary contact phone">
                <input type="tel" value={form.primaryContactPhone} data-testid="q-primaryContactPhone" onChange={(e) => set('primaryContactPhone', e.target.value)} className={inputCls} />
              </QField>
            </div>
            <QField label="Primary contact role (optional)">
              <input value={form.primaryContactRole} data-testid="q-primaryContactRole" onChange={(e) => set('primaryContactRole', e.target.value)} className={inputCls} />
            </QField>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QField label="Backup contact name (optional)">
                <input value={form.backupContactName} data-testid="q-backupContactName" onChange={(e) => set('backupContactName', e.target.value)} className={inputCls} />
              </QField>
              <QField label="Backup contact phone (optional)">
                <input type="tel" value={form.backupContactPhone} data-testid="q-backupContactPhone" onChange={(e) => set('backupContactPhone', e.target.value)} className={inputCls} />
              </QField>
            </div>

            <QField label="Parking / arrival instructions">
              <textarea rows={2} value={form.parkingInstructions} data-testid="q-parkingInstructions" onChange={(e) => set('parkingInstructions', e.target.value)} className={inputCls} />
            </QField>
            <QField label="Event description">
              <textarea rows={3} value={form.eventDescription} data-testid="q-eventDescription" onChange={(e) => set('eventDescription', e.target.value)} className={inputCls} />
            </QField>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QField label="Category">
                <select value={form.eventCategory} data-testid="q-eventCategory" onChange={(e) => set('eventCategory', e.target.value)} className={inputCls}>
                  {MEDIA_EVENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </QField>
              <QField label="Tone">
                <select value={form.tone} data-testid="q-tone" onChange={(e) => set('tone', e.target.value)} className={inputCls}>
                  {MEDIA_EVENT_TONES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </QField>
            </div>

            <QField label="Priority shots (1–2 highlights they care most about)">
              <input value={form.priorityShots} data-testid="q-priorityShots" onChange={(e) => set('priorityShots', e.target.value)} className={inputCls} />
            </QField>
            <QField label="Equipment notes (optional)">
              <input value={form.equipmentNotes} data-testid="q-equipmentNotes" onChange={(e) => set('equipmentNotes', e.target.value)} className={inputCls} />
            </QField>

            <div className="px-3 py-2.5 rounded-lg border border-white/[0.06] bg-white/[0.02] text-[12px] text-gray-400">
              Saving updates the request only — it does <strong className="text-gray-200">not</strong> approve it. Approval is its own step.
            </div>
          </>
        )}
        <Footer onClose={onClose} submitting={submitting || loading || !form} submitLabel="Save changes" />
      </form>
    </Modal>
  );
}

// ---------------- Package overrides ----------------

export function PackageOverrides({ pkg, onChanged }) {
  const [open, setOpen] = useState(null); // 'extend' | 'status'
  const close = () => setOpen(null);
  return (
    <div className="flex items-center gap-1.5">
      <button type="button" className={chipBtn} onClick={() => setOpen('extend')}><CalendarPlus className="w-3 h-3" /> Extend</button>
      <button type="button" className={chipBtn} onClick={() => setOpen('status')}><ToggleLeft className="w-3 h-3" /> Status</button>
      {open === 'extend' && <ExtendModal pkg={pkg} onChanged={onChanged} onClose={close} />}
      {open === 'status' && <PackageStatusModal pkg={pkg} onChanged={onChanged} onClose={close} />}
    </div>
  );
}

function ExtendModal({ pkg, onChanged, onClose }) {
  const [expiresOn, setExpiresOn] = useState((pkg.expiresOn || '').slice(0, 10));
  const { submitting, error, run } = useSubmit(onChanged, onClose);
  const onSubmit = (e) => {
    e.preventDefault();
    run(() => api.patch(`/api/admin/media/packages/${pkg.id}/extend`, { expiresOn }));
  };
  return (
    <Modal title="Extend expiration" icon={CalendarPlus} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-5 py-4">
        <ErrBanner text={error} />
        <label className={labelCls}>New expiration date</label>
        <DateInput required value={expiresOn} onChange={setExpiresOn} className={inputCls} />
        <Footer onClose={onClose} submitting={submitting} submitLabel="Extend" />
      </form>
    </Modal>
  );
}

function PackageStatusModal({ pkg, onChanged, onClose }) {
  const [status, setStatus] = useState(pkg.status === 'suspended' || pkg.status === 'cancelled' ? pkg.status : 'active');
  const { submitting, error, run } = useSubmit(onChanged, onClose);
  const onSubmit = (e) => {
    e.preventDefault();
    run(() => api.patch(`/api/admin/media/packages/${pkg.id}/status`, { status }));
  };
  return (
    <Modal title="Change package status" icon={ToggleLeft} onClose={onClose}>
      <form onSubmit={onSubmit} className="px-5 py-4">
        <ErrBanner text={error} />
        <label className={labelCls}>Status</label>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className={inputCls}>
          <option value="active">Active</option>
          <option value="suspended">Suspended</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <Footer onClose={onClose} submitting={submitting} submitLabel="Update status" />
      </form>
    </Modal>
  );
}

// datetime-local helpers (YYYY-MM-DDTHH:MM <-> ISO)
const toLocal = isoToLocal;
const fromLocal = localToIso;
